import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getOrderQuery } from '@/api/queries';
import { FirebaseClient } from '@/api/firebaseClient';
import { useEvent } from '@/layouts/eventContext';
import { formatEventNumericDate } from '@/features/events/eventFormatting';
import { formatEur } from '@/features/ticketing/format';

const client = new FirebaseClient();

export const EventOrderDetails = () => {
  const { t, i18n } = useTranslation();
  const event = useEvent();
  const { orderId } = useParams<{ orderId: string }>();
  const { data } = useQuery(getOrderQuery(orderId ?? ''));

  if (!data) return null;
  const { order, tickets } = data;
  const paid = Boolean(order.paidAt);
  const serviceFee = order.serviceFee ?? 0;
  const hasFee = serviceFee > 0;
  const total = order.total ?? order.price;

  const makePayment = async () => {
    const res = await client.getPaymentUrl(order.orderId);
    location.href = res.data.url;
  };

  if (order.canceledAt) {
    return (
      <div className="relative z-[2] px-5 py-11 sm:px-12">
        <div className="ev-glass max-w-[640px] p-7" style={{ borderColor: 'var(--ev-accent2)' }}>
          <div className="ev-disp" style={{ fontSize: 32 }}>
            {t('orderCanceled')}
          </div>
          <p className="ev-mono mt-2 text-sm" style={{ color: 'var(--ev-muted)' }}>
            {t('ticketCanceled_message')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-[2] mx-auto w-full max-w-[760px] px-5 py-11 sm:px-12">
      <div className="ev-eyebrow">{t('orderStatus')}</div>
      <h1 className="ev-disp uppercase" style={{ fontSize: 50, marginTop: 8, marginBottom: 24 }}>
        {t('your_order')}
      </h1>

      {!paid ? (
        <div
          className="mb-7 flex flex-wrap items-center justify-between gap-4 p-6"
          style={{ background: 'var(--ev-accent2)', color: 'var(--ev-ink)' }}>
          <div>
            <div className="ev-disp" style={{ fontSize: 22 }}>
              {t('paymentPending')}
            </div>
            <p className="mt-1 text-sm">{t('paymentPending_message')}</p>
          </div>
          <button type="button" onClick={makePayment} className="ev-btn">
            {t('payNow')} →
          </button>
        </div>
      ) : null}

      <dl className="mb-2">
        {paid ? (
          <div
            className="flex justify-between py-3"
            style={{ borderBottom: '1px solid var(--ev-line)' }}>
            <dt
              className="ev-mono uppercase"
              style={{ fontSize: 12, letterSpacing: '.08em', color: 'var(--ev-muted)' }}>
              {t('paymentReceivedOn')}
            </dt>
            <dd className="whitespace-nowrap" style={{ margin: 0, color: 'var(--ev-accent)' }}>
              {formatEventNumericDate(order.paidAt)}
            </dd>
          </div>
        ) : null}
        <div
          className="flex justify-between py-3"
          style={{ borderBottom: '1px solid var(--ev-line)' }}>
          <dt
            className="ev-mono uppercase"
            style={{ fontSize: 12, letterSpacing: '.08em', color: 'var(--ev-muted)' }}>
            {t('orderNumber')}
          </dt>
          <dd className="ev-mono" style={{ margin: 0, color: 'var(--ev-ink)' }}>
            {order.orderId}
          </dd>
        </div>
        <div
          className="flex justify-between py-3"
          style={{ borderBottom: '1px solid var(--ev-line)' }}>
          <dt
            className="ev-mono uppercase"
            style={{ fontSize: 12, letterSpacing: '.08em', color: 'var(--ev-muted)' }}>
            E-Mail
          </dt>
          <dd style={{ margin: 0, color: 'var(--ev-ink)' }}>{order.email}</dd>
        </div>
        {hasFee ? (
          <>
            <div
              className="flex items-baseline justify-between py-3"
              style={{ borderBottom: '1px solid var(--ev-line)' }}>
              <dt
                className="ev-mono uppercase"
                style={{ fontSize: 12, letterSpacing: '.08em', color: 'var(--ev-muted)' }}>
                {t('orderPrice')}
              </dt>
              <dd
                className="ev-mono whitespace-nowrap"
                style={{ margin: 0, color: 'var(--ev-ink)' }}>
                {formatEur(order.price, i18n.language)}
              </dd>
            </div>
            <div
              className="flex items-baseline justify-between py-3"
              style={{ borderBottom: '1px solid var(--ev-line)' }}>
              <dt
                className="ev-mono uppercase"
                style={{ fontSize: 12, letterSpacing: '.08em', color: 'var(--ev-muted)' }}>
                {t('service_fee', { percent: order.serviceFeePercent })}
              </dt>
              <dd
                className="ev-mono whitespace-nowrap"
                style={{ margin: 0, color: 'var(--ev-ink)' }}>
                {formatEur(serviceFee, i18n.language)}
              </dd>
            </div>
          </>
        ) : null}
        <div
          className="flex items-baseline justify-between py-3"
          style={{ borderBottom: '1px solid var(--ev-line)' }}>
          <dt
            className="ev-mono uppercase"
            style={{ fontSize: 12, letterSpacing: '.08em', color: 'var(--ev-muted)' }}>
            {hasFee ? t('grand_total') : t('orderPrice')}
          </dt>
          <dd
            className="ev-disp ev-accent-text whitespace-nowrap"
            style={{ margin: 0, fontSize: 30 }}>
            {formatEur(total, i18n.language)}
          </dd>
        </div>
      </dl>

      <h2 className="ev-disp mb-4 mt-8 uppercase" style={{ fontSize: 24 }}>
        {t('tickets')}
      </h2>
      <div className="flex flex-col gap-3">
        {tickets.map((ticket) => (
          <div
            key={ticket.ticketId}
            className="ev-panel flex items-center justify-between gap-4 p-4">
            <div className="flex flex-wrap items-baseline gap-3.5">
              <Link
                to={`/events/${event.slug}/tickets/${ticket.ticketId}`}
                className="font-sans text-lg font-semibold no-underline"
                style={{ color: 'var(--ev-ink)' }}>
                {ticket.name}
              </Link>
              <span className="ev-mono text-sm" style={{ color: 'var(--ev-muted)' }}>
                {ticket.ticketType.label}
              </span>
              <Link
                to={`/events/${event.slug}/tickets/${ticket.ticketId}`}
                className="ev-mono text-xs uppercase tracking-[0.06em]"
                style={{ color: 'var(--ev-accent)' }}>
                {t('showTicket')} →
              </Link>
            </div>
            <span
              className="ev-mono whitespace-nowrap text-xl font-bold"
              style={{ color: 'var(--ev-ink)' }}>
              {formatEur(ticket.ticketType.price, i18n.language)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
